import { supabase } from './supabase'
import type { Database } from '@/types/database-extensions'

// Define Meetup types based on the database schema
// These should be added to the supabase.ts types file later
export interface Meetup {
  id: string
  created_at: string
  updated_at: string
  title: string
  description: string | null
  location: string | null
  address: string | null
  start_time: string
  end_time: string
  host_id: string
  is_private: boolean
  cover_image_url: string | null
  status: string
  limit_time_range?: boolean
  daily_start_time?: string | null
  daily_end_time?: string | null
  signup_start_time?: string | null
}

export interface MeetupInsert
  extends Omit<Database['public']['Tables']['meetups']['Insert'], 'created_at' | 'updated_at'> {
  add_placeholders?: boolean
  limit_time_range?: boolean
  daily_start_time?: string | null
  daily_end_time?: string | null
  signup_start_time?: string | null
}

export interface MeetupUpdate {
  id?: string
  created_at?: string
  updated_at?: string
  title?: string
  description?: string | null
  location?: string | null
  address?: string | null
  start_time?: string
  end_time?: string
  host_id?: string
  is_private?: boolean
  cover_image_url?: string | null
  status?: string
  add_placeholders?: boolean
  limit_time_range?: boolean
  daily_start_time?: string | null
  daily_end_time?: string | null
  signup_start_time?: string | null
}

export interface MeetupQueryParams {
  hostId?: string
  limit?: number
  startDate?: string
  status?: string
}

/**
 * Creates placeholder events for setup and cleanup for a meetup
 * @param meetupData - The meetup data to create placeholders for
 * @param meetupId - The ID of the created meetup
 * @returns Object containing any errors that occurred
 */
async function createPlaceholderEvents(meetupData: MeetupInsert, meetupId: string) {
  try {
    const startDate = new Date(meetupData.start_time)
    const endDate = new Date(meetupData.end_time)

    // Create pre-game setup placeholder (1 hour before meetup starts)
    const setupStartTime = new Date(startDate)
    setupStartTime.setHours(startDate.getHours() - 1)

    const setupEvent = {
      title: 'Placeholder for setup',
      description: 'Time reserved for setting up the meetup',
      location: meetupData.location || null,
      address: meetupData.address || null,
      start_time: setupStartTime.toISOString(),
      end_time: startDate.toISOString(),
      host_id: meetupData.host_id,
      is_private: meetupData.is_private || false,
      meetup_id: meetupId,
      status: 'scheduled',
      metadata: {
        game: {
          name: 'Setup',
          image_url: '/images/setup-placeholder.svg',
        },
        isPlaceholder: true,
        placeholderType: 'setup',
        customStyle: {
          backgroundColor: '#333333', // Darker grey
          borderColor: '#444444', // Dark grey for border
          textColor: '#FFFFFF', // White text for contrast
        },
      },
    }

    // Create post-game cleanup placeholder (1 hour after meetup ends)
    const cleanupEndTime = new Date(endDate)
    cleanupEndTime.setHours(endDate.getHours() + 1)

    const cleanupEvent = {
      title: 'Placeholder for cleanup',
      description: 'Time reserved for cleaning up after the meetup',
      location: meetupData.location || null,
      address: meetupData.address || null,
      start_time: endDate.toISOString(),
      end_time: cleanupEndTime.toISOString(),
      host_id: meetupData.host_id,
      is_private: meetupData.is_private || false,
      meetup_id: meetupId,
      status: 'scheduled',
      metadata: {
        game: {
          name: 'Cleanup',
          image_url: '/images/cleanup-placeholder.svg',
        },
        isPlaceholder: true,
        placeholderType: 'cleanup',
        customStyle: {
          backgroundColor: '#333333', // Darker grey
          borderColor: '#444444', // Dark grey for border
          textColor: '#FFFFFF', // White text for contrast
        },
      },
    }

    // Import here to avoid circular dependencies
    const { createEvent } = await import('./events')

    // Create both events
    const [setupResult, cleanupResult] = await Promise.all([
      createEvent(setupEvent as any),
      createEvent(cleanupEvent as any),
    ])

    // Log any errors but don't fail the meetup creation
    if (setupResult.error) {
      console.error('Error creating setup placeholder event:', setupResult.error)
    }

    if (cleanupResult.error) {
      console.error('Error creating cleanup placeholder event:', cleanupResult.error)
    }

    return {
      setupEventId: setupResult.data?.id,
      cleanupEventId: cleanupResult.data?.id,
      error: setupResult.error || cleanupResult.error,
    }
  } catch (error) {
    console.error('Error creating placeholder events:', error)
    return {
      setupEventId: null,
      cleanupEventId: null,
      error: error instanceof Error ? error.message : 'Failed to create placeholder events',
    }
  }
}

/**
 * Updates placeholder events for setup and cleanup for a meetup
 * This will delete existing placeholders if found, and create new ones
 * @param meetupData - The updated meetup data
 * @param meetupId - The ID of the meetup
 * @returns Object containing any errors that occurred
 */
async function updatePlaceholderEvents(meetupData: MeetupUpdate, meetupId: string) {
  try {
    // First, check if we have the necessary time fields to update placeholder events
    if (!meetupData.start_time && !meetupData.end_time) {
      // No time update, no need to update placeholder events
      return { error: null }
    }

    // Check if add_placeholders is present and set to false
    const addPlaceholders =
      meetupData.add_placeholders !== undefined ? meetupData.add_placeholders : true // Default to true if not specified

    if (!addPlaceholders) {
      return { error: null } // Don't create placeholders if disabled
    }

    // Import here to avoid circular dependencies
    const { createEvent } = await import('./events')

    // Get the full meetup data with current events
    const { data: meetupWithEvents, error: meetupError } = await getMeetupById(meetupId)
    if (meetupError || !meetupWithEvents) {
      return { error: meetupError || 'Failed to retrieve meetup data' }
    }

    // Find existing placeholder events
    const placeholderEvents = (meetupWithEvents.events || []).filter(
      (event: { title: string; id: string }) =>
        event.title === 'Placeholder for setup' || event.title === 'Placeholder for cleanup'
    )

    // Delete existing placeholder events if any
    if (placeholderEvents.length > 0) {
      const { deleteEvent } = await import('./events')
      await Promise.all(placeholderEvents.map((event: { id: string }) => deleteEvent(event.id)))
    }

    // Create new placeholder events with updated times
    // We'll create a "fake" meetupData object that includes host_id from the existing data
    const fullMeetupData = {
      ...meetupWithEvents,
      ...meetupData, // Overwrite with any updated fields
      host_id: meetupWithEvents.host?.id || meetupWithEvents.host_id,
      add_placeholders: addPlaceholders, // Use the processed value
    }

    return createPlaceholderEvents(fullMeetupData, meetupId)
  } catch (error) {
    console.error('Error updating placeholder events:', error)
    return {
      error: error instanceof Error ? error.message : 'Failed to update placeholder events',
    }
  }
}

/**
 * Creates a new meetup
 */
export async function createMeetup(meetupData: MeetupInsert) {
  try {
    // First, ensure user is authenticated and get the actual auth user
    const { data: sessionData } = await supabase.auth.getSession()
    console.log('Current session:', sessionData?.session ? 'Active' : 'None')

    const { data: userData, error: userError } = await supabase.auth.getUser()
    if (userError) {
      console.error('Authentication error:', userError)
      throw new Error(`Authentication error: ${userError.message}`)
    }

    if (!userData?.user) {
      console.error('No authenticated user found')
      throw new Error('User is not authenticated')
    }

    console.log('Authenticated user ID:', userData.user.id)
    console.log('Provided host_id:', meetupData.host_id)

    // Explicitly validate the host_id matches the current user
    if (userData.user.id !== meetupData.host_id) {
      console.error(
        'Host ID mismatch - auth user:',
        userData.user.id,
        'provided host_id:',
        meetupData.host_id
      )
      throw new Error('User ID does not match host_id')
    }

    // Create a clean meetup object with only the fields allowed in the database
    // This avoids any unexpected fields that might cause RLS issues
    const meetupForInsert = {
      title: meetupData.title,
      description: meetupData.description || null,
      location: meetupData.location || null,
      address: meetupData.address || null,
      start_time: meetupData.start_time,
      end_time: meetupData.end_time,
      host_id: userData.user.id, // Always use the authenticated user ID
      is_private: meetupData.is_private === true, // Ensure boolean
      cover_image_url: meetupData.cover_image_url || null,
      status: meetupData.status || 'draft',
      limit_time_range: meetupData.limit_time_range || false,
      daily_start_time: meetupData.daily_start_time || null,
      daily_end_time: meetupData.daily_end_time || null,
      signup_start_time: meetupData.signup_start_time || null,
    }

    // Log full details for troubleshooting
    console.log('Creating meetup with validated data:', meetupForInsert)

    // Create the meetup in Supabase
    const { data, error } = await supabase.from('meetups').insert(meetupForInsert).select().single()

    if (error) {
      console.error('Supabase insert error:', error)
      throw new Error(`Failed to create meetup: ${error.message}`)
    }

    // Process placeholders separately (not part of the DB schema)
    const addPlaceholders =
      meetupData.add_placeholders !== undefined ? meetupData.add_placeholders : true

    if (data && addPlaceholders) {
      console.log('Creating placeholder events for meetup ID:', data.id)
      await createPlaceholderEvents(meetupData, data.id)
    }

    return { data, error: null }
  } catch (error) {
    console.error('Error creating meetup:', error)
    return {
      data: null,
      error:
        error instanceof Error
          ? error.message
          : 'An unknown error occurred while creating the meetup',
    }
  }
}

/**
 * Updates an existing meetup
 */
export async function updateMeetup(id: string, updates: MeetupUpdate) {
  try {
    // First, ensure user is authenticated
    const { data: sessionData } = await supabase.auth.getSession()
    console.log('Current session:', sessionData?.session ? 'Active' : 'None')

    const { data: userData, error: userError } = await supabase.auth.getUser()
    if (userError) {
      console.error('Authentication error:', userError)
      throw new Error(`Authentication error: ${userError.message}`)
    }

    if (!userData?.user) {
      console.error('No authenticated user found')
      throw new Error('User is not authenticated')
    }

    // First fetch the meetup to check permissions
    const { data: existingMeetup, error: fetchError } = await supabase
      .from('meetups')
      .select('host_id')
      .eq('id', id)
      .single()

    if (fetchError) {
      console.error('Error fetching meetup:', fetchError)
      throw new Error(`Failed to fetch meetup: ${fetchError.message}`)
    }

    if (!existingMeetup) {
      throw new Error('Meetup not found')
    }

    // Verify the authenticated user is the host
    if (existingMeetup.host_id !== userData.user.id) {
      console.error('User is not the host of this meetup')
      throw new Error('Only the host can update this meetup')
    }

    // Clean the data to avoid storing placeholder strings
    const updateData: Record<string, any> = {}

    if (updates.title !== undefined) updateData.title = updates.title.trim()
    if (updates.description !== undefined) {
      updateData.description = updates.description ? updates.description.trim() : null
    }
    if (updates.location !== undefined) {
      updateData.location = updates.location ? updates.location.trim() : null
    }
    if (updates.address !== undefined) {
      updateData.address = updates.address ? updates.address.trim() : null
    }
    if (updates.start_time !== undefined) updateData.start_time = updates.start_time
    if (updates.end_time !== undefined) updateData.end_time = updates.end_time
    if (updates.is_private !== undefined) updateData.is_private = updates.is_private === true
    if (updates.cover_image_url !== undefined) updateData.cover_image_url = updates.cover_image_url
    if (updates.status !== undefined) updateData.status = updates.status
    if (updates.limit_time_range !== undefined)
      updateData.limit_time_range = updates.limit_time_range === true
    if (updates.daily_start_time !== undefined)
      updateData.daily_start_time = updates.daily_start_time || null
    if (updates.daily_end_time !== undefined)
      updateData.daily_end_time = updates.daily_end_time || null
    if (updates.signup_start_time !== undefined)
      updateData.signup_start_time = updates.signup_start_time || null

    // Log the update data for troubleshooting
    console.log('Updating meetup with data:', updateData)

    // Perform the update
    const { data, error } = await supabase
      .from('meetups')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Supabase update error:', error)
      throw new Error(`Failed to update meetup: ${error.message}`)
    }

    // Handle placeholder events separately
    if (data) {
      // We'll use the original updates object for local processing
      const addPlaceholders =
        updates.add_placeholders !== undefined ? updates.add_placeholders : true

      // Update placeholder events if time fields have changed
      if (updates.start_time || updates.end_time || updates.add_placeholders !== undefined) {
        console.log('Updating placeholder events for meetup:', id)
        await updatePlaceholderEvents({ ...updates, add_placeholders: addPlaceholders }, id)
      }
    }

    return { data, error: null }
  } catch (error) {
    console.error('Error updating meetup:', error)
    return {
      data: null,
      error:
        error instanceof Error
          ? error.message
          : 'An unknown error occurred while updating the meetup',
    }
  }
}

/**
 * Deletes a meetup
 */
export async function deleteMeetup(id: string) {
  try {
    // Use our custom RPC function to disconnect events from the meetup
    const { error: rpcError } = await supabase.rpc('update_events_meetup_id', {
      target_meetup_id: id,
      new_meetup_id: null,
    })

    if (rpcError) {
      console.error('RPC error:', rpcError)

      // Fallback: Try a direct update if the RPC fails
      console.log('Trying direct SQL update as fallback')
      const { error: directUpdateError } = await supabase
        .from('events')
        .update({ meetup_id: null })
        .eq('meetup_id', id)

      if (directUpdateError) {
        throw new Error(`Failed to update event associations: ${directUpdateError.message}`)
      }
    }

    // Verify all events are disconnected
    const { count, error: countError } = await supabase
      .from('events')
      .select('id', { count: 'exact', head: true })
      .eq('meetup_id', id)

    if (countError) {
      console.error('Count verification error:', countError)
    } else if (count && count > 0) {
      console.error(`Warning: ${count} events still linked to meetup after update attempt`)
      // We'll still try to delete even with this warning
    }

    // Now delete the meetup
    const { error: deleteError } = await supabase.from('meetups').delete().eq('id', id)

    if (deleteError) {
      throw new Error(`Failed to delete meetup: ${deleteError.message}`)
    }

    return { success: true, error: null }
  } catch (error) {
    console.error('Error deleting meetup:', error)
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'An unknown error occurred while deleting the meetup',
    }
  }
}

/**
 * Gets a meetup by ID
 */
export async function getMeetupById(id: string) {
  try {
    const { data, error } = await supabase
      .from('meetups')
      .select(
        `
        *,
        host:profiles!meetups_host_id_fkey(id, username, full_name, avatar_url),
        events(
          id,
          title,
          start_time,
          end_time,
          status
        )
      `
      )
      .eq('id', id)
      .single()

    if (error) throw new Error(`Failed to fetch meetup: ${error.message}`)

    return { data, error: null }
  } catch (error) {
    console.error('Error fetching meetup:', error)
    return {
      data: null,
      error:
        error instanceof Error
          ? error.message
          : 'An unknown error occurred while fetching the meetup',
    }
  }
}

/**
 * Gets meetups with optional filtering
 */
export async function getMeetups(params?: MeetupQueryParams) {
  try {
    let query = supabase
      .from('meetups')
      .select(
        `
        *,
        host:profiles(id, full_name, avatar_url, username)
      `
      )
      .order('start_time', { ascending: true })

    // Apply filters if provided
    if (params?.hostId) {
      query = query.eq('host_id', params.hostId)
    }

    if (params?.startDate) {
      query = query.gte('start_time', params.startDate)
    }

    if (params?.status) {
      query = query.eq('status', params.status)
    }

    if (params?.limit) {
      query = query.limit(params.limit)
    }

    const { data, error } = await query

    if (error) throw new Error(error.message)

    return { data, error: null }
  } catch (err) {
    console.error('Error in getMeetups:', err)
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Failed to fetch meetups',
    }
  }
}

/**
 * Gets meetups by host ID
 */
export async function getMeetupsByHostId(hostId: string, limit?: number) {
  return getMeetups({ hostId, limit })
}

/**
 * Gets events for a specific meetup
 */
export async function getMeetupEvents(
  meetupId: string,
  {
    startDate,
    endDate,
    status,
    limit = 20,
    offset = 0,
  }: {
    startDate?: string | Date
    endDate?: string | Date
    status?: Database['public']['Enums']['event_status']
    limit?: number
    offset?: number
  } = {}
) {
  try {
    // Build the query
    let dbQuery = supabase
      .from('events')
      .select(
        `
        *,
        host:profiles!events_host_id_fkey(id, username, full_name, avatar_url),
        participant_count:event_participants(count)
      `,
        { count: 'exact' }
      )
      .eq('meetup_id', meetupId)

    // Apply filters
    if (startDate) {
      const startDateStr = startDate instanceof Date ? startDate.toISOString() : startDate
      dbQuery = dbQuery.gte('start_time', startDateStr)
    }

    if (endDate) {
      const endDateStr = endDate instanceof Date ? endDate.toISOString() : endDate
      dbQuery = dbQuery.lte('start_time', endDateStr)
    }

    if (status) {
      dbQuery = dbQuery.eq('status', status)
    }

    // Apply pagination
    dbQuery = dbQuery.range(offset, offset + limit - 1)

    // Order by start time
    dbQuery = dbQuery.order('start_time', { ascending: true })

    const { data, error, count } = await dbQuery

    if (error) {
      console.error('Supabase query error:', error)
      throw new Error(`Failed to fetch meetup events: ${error.message}`)
    }

    // If we have events and game_ids, fetch the game data separately
    if (data && data.length > 0) {
      // Get unique game IDs from events
      const gameIds = data.filter(event => event.game_id).map(event => event.game_id)

      if (gameIds.length > 0) {
        // Fetch games data
        const { data: gamesData, error: gamesError } = await supabase
          .from('games')
          .select('*')
          .in('id', gameIds)

        if (!gamesError && gamesData) {
          // Create a map of game data by ID for quick lookup
          const gamesMap = gamesData.reduce((map, game) => {
            map[game.id] = game
            return map
          }, {} as Record<string, any>)

          // Add game data to each event
          data.forEach(event => {
            if (event.game_id && gamesMap[event.game_id]) {
              event.game = gamesMap[event.game_id]
            }
          })
        } else {
          console.warn('Failed to fetch game data:', gamesError)
        }
      }
    }

    return { data, count, error: null }
  } catch (error) {
    console.error('Error fetching meetup events:', error)
    return {
      data: [],
      count: 0,
      error:
        error instanceof Error
          ? error.message
          : 'An unknown error occurred while fetching meetup events',
    }
  }
}

/**
 * Sets up a real-time subscription for meetups
 */
export function subscribeToMeetups(callback: (payload: any) => void) {
  const subscription = supabase
    .channel('meetups-channel')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'meetups',
      },
      callback
    )
    .subscribe()

  // Return unsubscribe function
  return () => {
    subscription.unsubscribe()
  }
}
